/* Created by Interactive 63 - https://interactive63.com */
/**
 * The stage panel: renders the project's SVG inside a shadow root so its styles cannot leak into
 * the editor, provides zoom, pan, selection and the move, rotate and scale tools, and draws the
 * overlay (outlines, handles, pivot rulers, snap guides and the motion path).
 */
import {
  GEOMETRY_TAGS,
  type ElementId,
  type PropertyValue,
  type Vec2,
} from '../../../core/model/types';
import {
  selectElements,
  clearSelection,
  setElementOrigin,
  setHovered,
  setStageView,
} from '../../store/actions';
import { Component } from '../../ui/component';
import type { Store } from '../../store/store';
import { h } from '../../ui/dom';
import { isTextEntryTarget } from '../../shortcuts';
import { createPopover } from '../../ui/popover';
import { readStorage, writeStorage } from '../../storage';
import { showContextMenu, type MenuItem } from '../timeline/context-menu';
import type { InspectorContext } from '../inspector/inspector-context';
import {
  applyMatrix,
  originFromLocal,
  pivotLocal,
  roundTo,
  type Matrix,
  type Point,
  type ScaleAxes,
} from './geometry';
import { Manipulator, type ManipulationServices } from './manipulate';
import { TEXT_TAGS } from '../../../core/text/text';
import { requestTextEditing } from '../../text-editing';
import { transformPath } from '../../../core/morph/bounds';
import { originalPathData } from '../../../core/morph/convert';
import { buildMorphGuides } from './morph-guides';
import { buildMotionPath } from './motion-path';
import { PointEditor } from './point-edit';
import type { NodeRef, SegmentRef } from '../../../core/path/edit';
import { toggleSmooth } from '../../store/motion-actions';
import { Overlay, EMPTY_SCENE, type OverlayHost, type OverlayScene, type Ruler } from './overlay';
import {
  computeRulers,
  containsPoint,
  isGeometry,
  shapeBoxInLayer,
  shapeCandidates,
  type MeasureTargets,
  type RulerSource,
} from './rulers';
import { screenCtmIncludesZoom } from './ctm-check';
import type { ScreenBox } from './snap';
import { resolveSelectionTarget, toggleInSelection, type ClickKind } from './selection';
import { beginArtboardDrag } from './artboard-drag';
import { centreView, fitView, wheelZoomFactor, zoomAt, type Size } from './view-math';
import type { ArtboardHandle, Box } from '../../../core/model/artboard';

const ZOOM_STEP = 1.25;
const MEASURE_KEY = 'svg-tempo:measure';
/** Remembers whether picked morph shapes move to their layer or stay in place. */
const PLACEMENT_STORAGE_KEY = 'svg-tempo:morph-placement';

/** The shape chosen in pick mode and what to do with it. */
export interface PickResult {
  id: ElementId;
  hide: boolean;
  placement: 'world' | 'in-place';
}

export type Tool = 'select' | 'rotate' | 'scale';

/** The key held to show the pivot rulers. */
const MEASURE_HOLD_KEY = '4';

const TOOLS: { id: Tool; label: string; key: string }[] = [
  { id: 'select', label: 'Select', key: '1' },
  { id: 'rotate', label: 'Rotate', key: '2' },
  { id: 'scale', label: 'Scale', key: '3' },
];

const ARROW_STEPS: Record<string, Point> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};

const STAGE_CSS = `
  :host { display: block; position: absolute; inset: 0; overflow: hidden; }
  .artboard { position: absolute; left: 0; top: 0; transform-origin: 0 0; outline: 1px solid rgb(128 128 128 / 0.5); }
  .artboard > svg { display: block; width: 100%; height: 100%; overflow: visible; }
`;

export class StagePanel
  extends Component
  implements OverlayHost, ManipulationServices, RulerSource
{
  /** Public so the manipulation, ruler and motion path helpers can read project state. */
  declare public store: Store;
  private viewport!: HTMLElement;
  private shadow!: ShadowRoot;
  private artboard!: HTMLElement;
  private hiddenStyle!: HTMLStyleElement;
  private compiledStyle!: HTMLStyleElement;
  private lastMarkup = '';
  private sceneChangeListeners = new Set<() => void>();
  private overlay!: Overlay;
  private zoomLabel!: HTMLButtonElement;
  private elementsById = new Map<ElementId, Element>();
  private pendingFit = false;
  private panDrag: { pointerId: number; x: number; y: number } | null = null;
  private hoverFrame = 0;
  private manipulator!: Manipulator;
  private picking: ((result: PickResult | null) => void) | null = null;
  private pickHide!: HTMLInputElement;
  private pickPlacement!: HTMLSelectElement;
  private pickBanner!: HTMLElement;
  private pointBanner!: HTMLElement;
  private pointBannerText!: HTMLElement;
  private pointKeyButton!: HTMLButtonElement;
  private pointEditor!: PointEditor;
  private tool: Tool = 'select';
  private toolButtons = new Map<Tool, HTMLButtonElement>();
  private targets: MeasureTargets = { layer: true, shape: true, neighbour: true, artboard: true };
  private alwaysMeasure = readStorage(MEASURE_KEY) === 'on';
  private measureHeld = false;
  private busy: ElementId[] = [];
  private guides: { x: number[]; y: number[] } | null = null;
  private readout: OverlayScene['readout'] = null;
  private sceneFrame: { projectId: string; x: number; y: number } | null = null;
  private lastSize = { width: 0, height: 0 };
  private chips!: HTMLElement;
  private shapeName!: HTMLElement;
  private ctmIncludesZoom = true;
  private editing:
    Pick<InspectorContext, 'time' | 'isPlaying' | 'readValue' | 'playback'> | undefined;

  /** Creates the panel section. */
  protected override createRoot(): HTMLElement {
    return h('section', { class: ['panel', 'panel-stage'], 'aria-label': 'Stage' });
  }

  /** Builds the toolbar, viewport and shadow root, then wires the store and input handlers. */
  protected override init(): void {
    const host = h('div', { class: 'stage-host' });
    this.shadow = host.attachShadow({ mode: 'open' });
    this.artboard = h('div', { class: 'artboard' });
    this.hiddenStyle = h('style');
    this.compiledStyle = h('style');
    this.shadow.append(
      h('style', null, STAGE_CSS),
      this.hiddenStyle,
      this.compiledStyle,
      this.artboard,
    );

    this.viewport = h(
      'div',
      {
        class: 'stage-viewport stage-bg-checker',
        tabindex: 0,
        role: 'group',
        'aria-label': 'Stage canvas',
      },
      host,
    );
    this.overlay = new Overlay(this.viewport, this);
    this.chips = h('div', { class: 'measure-chips', role: 'status' });
    this.pickHide = h('input', { type: 'checkbox', checked: true });
    this.pickPlacement = h(
      'select',
      { 'aria-label': 'Where the picked shape goes' },
      h('option', { value: 'in-place' }, 'Morph in place'),
      h('option', { value: 'world' }, 'Move to where it is'),
    );
    this.pickPlacement.value =
      readStorage(PLACEMENT_STORAGE_KEY) === 'world' ? 'world' : 'in-place';
    this.pickPlacement.addEventListener('change', () => {
      writeStorage(PLACEMENT_STORAGE_KEY, this.pickPlacement.value);
    });
    this.pickBanner = h(
      'div',
      { class: 'pick-banner', role: 'status', hidden: true },
      h('span', null, 'Click a shape to morph into. Press Escape to cancel.'),
      h('label', null, this.pickHide, ' Hide the picked shape in the export'),
      h('label', null, 'Placement ', this.pickPlacement),
    );
    this.pointBannerText = h('span');
    const keyShape = h(
      'button',
      { type: 'button', title: 'Animate the shape: edits key it at the playhead from now on' },
      'Key shape',
    );
    keyShape.addEventListener('click', () => {
      this.pointEditor.keyShape();
    });
    this.pointKeyButton = keyShape;
    this.pointBanner = h(
      'div',
      { class: 'pick-banner point-banner', role: 'status', hidden: true },
      this.pointBannerText,
      keyShape,
    );
    this.viewport.append(this.overlay.el, this.chips, this.pickBanner, this.pointBanner);
    this.manipulator = new Manipulator(this);
    this.pointEditor = new PointEditor(this);
    this.ctmIncludesZoom = screenCtmIncludesZoom();

    this.el.append(h('h2', null, 'Stage'), this.buildToolbar(), this.viewport);

    this.bindStore();
    this.bindInput();

    const resizer = new ResizeObserver(() => {
      if (this.pendingFit && this.viewportSize().width > 0) this.fit();
      this.redrawOverlay();
    });
    resizer.observe(this.viewport);
    this.addCleanup(() => {
      resizer.disconnect();
    });
  }

  /** Returns the rendered shadow root, for previews and tests. */
  getShadowRoot(): ShadowRoot {
    return this.shadow;
  }

  /** Looks up the rendered element for an element ID. */
  getElement(id: ElementId): Element | undefined {
    return this.elementsById.get(id);
  }

  /** Whether the element is a gradient stop. */
  isStop(id: ElementId): boolean {
    return this.store.getState().project.elements[id]?.tag === 'stop';
  }

  /**
   * Enters pick mode: the next click on a shape chooses it. Resolves with the shape and whether
   * to hide it in the export, or null if Escape was pressed.
   */
  beginPick(): Promise<PickResult | null> {
    this.finishPick(null);
    this.pickBanner.hidden = false;
    this.viewport.classList.add('is-picking');
    this.viewport.focus({ preventScroll: true });
    return new Promise((resolve) => {
      this.picking = resolve;
    });
  }

  /** Leaves pick mode with a result. */
  private finishPick(result: PickResult | null): void {
    const resolve = this.picking;
    this.picking = null;
    this.pickBanner.hidden = true;
    this.viewport.classList.remove('is-picking');
    resolve?.(result);
  }

  /**
   * A shape's path data in another element's coordinates, where the two sit on the stage right
   * now. Animation counts: a layer moved with a position track is measured where it is at the
   * playhead, not where its markup says, so the result lands exactly over the picked shape.
   */
  pickedPath(intoId: ElementId, pickedId: ElementId): string | null {
    const into = this.elementsById.get(intoId) as SVGGraphicsElement | undefined;
    const picked = this.elementsById.get(pickedId) as SVGGraphicsElement | undefined;
    if (!into || !picked) return null;
    const d = originalPathData(this.store.getState().project.svg.source, pickedId);
    if (d === '') return null;
    const a = this.screenMatrix(into);
    const b = this.screenMatrix(picked);
    if (!a || !b) return d;
    const m = new DOMMatrix([a.a, a.b, a.c, a.d, a.e, a.f])
      .inverse()
      .multiply(new DOMMatrix([b.a, b.b, b.c, b.d, b.e, b.f]));
    return transformPath(d, [m.a, m.b, m.c, m.d, m.e, m.f]);
  }

  /** Supplies the playhead and value readers that edits are written with. */
  bindEditing(
    editing: Pick<InspectorContext, 'time' | 'isPlaying' | 'readValue' | 'playback'>,
  ): void {
    this.editing = editing;
  }

  /** The element whose box defines the pivot: its wrapper when one was generated for it. */
  private pivotTarget(rendered: Element): SVGGraphicsElement {
    const parent = rendered.parentElement;
    return (parent?.classList.contains('st-wrap') ? parent : rendered) as SVGGraphicsElement;
  }

  /** The node that is animated for an element: its wrapper, or the element itself. */
  node(id: ElementId): SVGGraphicsElement | undefined {
    const rendered = this.elementsById.get(id);
    return rendered ? this.pivotTarget(rendered) : undefined;
  }

  /**
   * A node's matrix to the screen. Browsers include the artboard's CSS zoom in this matrix; if
   * one does not, the zoom and position are applied here instead.
   */
  screenMatrix(node: SVGGraphicsElement): Matrix | null {
    if (this.ctmIncludesZoom) return node.getScreenCTM();
    const own = node.getCTM();
    if (!own) return null;
    const { stageZoom } = this.store.getState().editor;
    const rect = this.artboard.getBoundingClientRect();
    return new DOMMatrix().translate(rect.left, rect.top).scale(stageZoom).multiply(own);
  }

  /** The root `<svg>` element of the rendered scene. */
  rootSvg(): SVGSVGElement | null {
    return this.artboard.querySelector('svg');
  }

  /** Elements under a client point, topmost first. */
  elementsAt(x: number, y: number): Element[] {
    return this.shadow.elementsFromPoint(x, y);
  }

  /** Where an element pivots on screen, in client coordinates. */
  pivotScreen(id: ElementId): Point | null {
    return this.getOrigin(id);
  }

  /** The value an element shows for a property at the playhead. */
  readValue(id: ElementId, property: 'translate' | 'rotate' | 'scale'): PropertyValue {
    if (this.editing) return this.editing.readValue(id, property);
    return property === 'rotate' ? 0 : property === 'scale' ? { x: 1, y: 1 } : { x: 0, y: 0 };
  }

  /** The time edits are written at. */
  time(): number {
    return this.editing ? this.editing.time() : this.store.getState().editor.playhead;
  }

  /** Marks elements as being manipulated, which shows their pivot rulers. */
  setBusy(ids: readonly ElementId[]): void {
    this.busy = [...ids];
    this.redrawOverlay();
  }

  /** Sets the snap guides drawn during a move. */
  setGuides(guides: { x: number[]; y: number[] } | null): void {
    this.guides = guides;
  }

  /** Sets the live value shown beside the pointer. */
  setReadout(readout: OverlayScene['readout']): void {
    this.readout = readout;
    this.redrawOverlay();
  }

  /** The union of the elements' on-screen boxes. */
  selectionBox(ids: readonly ElementId[]): ScreenBox | null {
    let box: ScreenBox | null = null;
    for (const id of ids) {
      const el = this.elementsById.get(id);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;
      box = box
        ? {
            left: Math.min(box.left, r.left),
            top: Math.min(box.top, r.top),
            right: Math.max(box.right, r.right),
            bottom: Math.max(box.bottom, r.bottom),
          }
        : { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
    }
    return box;
  }

  /** Boxes a moving selection snaps to: other top-level elements, then the artboard. */
  snapTargets(excluding: readonly ElementId[]): ScreenBox[] {
    const { project } = this.store.getState();
    const skipped = new Set<ElementId>();
    for (const id of excluding) {
      for (let cur: ElementId | null | undefined = id; cur; cur = project.elements[cur]?.parentId) {
        skipped.add(cur);
      }
    }
    const boxes: ScreenBox[] = [];
    for (const id of project.rootIds) {
      if (skipped.has(id)) continue;
      const box = this.selectionBox([id]);
      if (box) boxes.push(box);
    }
    const a = this.artboard.getBoundingClientRect();
    boxes.push({ left: a.left, top: a.top, right: a.right, bottom: a.bottom });
    return boxes;
  }

  /** The centres of other shapes, in client coordinates. */
  shapeCentres(excluding: ElementId): Point[] {
    const centres: Point[] = [];
    for (const [otherId, other] of this.elementsById) {
      if (otherId === excluding || this.isStop(otherId)) continue;
      const centre = this.screenPoint(this.pivotTarget(other), 50, 50);
      if (centre) centres.push(centre);
    }
    return centres;
  }

  /**
   * Where the element pivots on screen. This mirrors the CSS the compiler writes: the origin is a
   * percentage of the element's own bounding box (or its wrapper's, when a wrapper exists), so
   * the marker shows exactly the point the browser will rotate and scale about.
   */
  getOrigin(id: ElementId): { x: number; y: number } | null {
    const rendered = this.elementsById.get(id);
    const meta = this.store.getState().project.elements[id];
    if (!rendered || !meta || meta.tag === 'stop') return null;
    return this.screenPoint(this.pivotTarget(rendered), meta.origin.x, meta.origin.y);
  }

  /** Maps a point given as percentages of an element's bounding box to client coordinates. */
  private screenPoint(target: SVGGraphicsElement, px: number, py: number): Point | null {
    try {
      const box = target.getBBox();
      const matrix = this.screenMatrix(target);
      if (!matrix) return null;
      return applyMatrix(matrix, pivotLocal(box, { x: px, y: py }));
    } catch {
      // Elements that are not rendered have no measurable box.
      return null;
    }
  }

  /** The element's bounding box corners on screen, carried through its current transforms. */
  getQuad(element: Element): { x: number; y: number }[] | null {
    const target = this.pivotTarget(element);
    try {
      const box = target.getBBox();
      if (box.width === 0 && box.height === 0) return null;
    } catch {
      return null;
    }
    const corners = [
      this.screenPoint(target, 0, 0),
      this.screenPoint(target, 100, 0),
      this.screenPoint(target, 100, 100),
      this.screenPoint(target, 0, 100),
    ];
    return corners.every((c) => c !== null) ? corners : null;
  }

  /** The element's own bounding box size in its local units, for the Inspector. */
  localSize(id: ElementId): { width: number; height: number } | null {
    const node = this.node(id);
    if (!node) return null;
    try {
      const box = node.getBBox();
      return { width: box.width, height: box.height };
    } catch {
      return null;
    }
  }

  /** Puts an element's origin back at the centre of its bounding box. */
  resetOrigin(id: ElementId): void {
    setElementOrigin(this.store, id, { x: 50, y: 50 });
  }

  /** Starts dragging an element's pivot. */
  beginOriginDrag(id: ElementId, down: PointerEvent): void {
    down.preventDefault();
    down.stopPropagation();
    this.manipulator.beginOrigin(id, down);
  }

  /** Starts rotating the selection from the rotate handle. */
  beginRotate(id: ElementId, down: PointerEvent): void {
    this.manipulator.beginRotate(this.manipulationIds(id), down);
  }

  /** Starts scaling the selection from a handle. */
  beginScale(id: ElementId, axes: ScaleAxes, down: PointerEvent): void {
    this.manipulator.beginScale(this.manipulationIds(id), axes, down);
  }

  /** The artboard's rectangle on screen, in client coordinates. */
  getArtboardRect(): { left: number; top: number; right: number; bottom: number } | null {
    const rect = this.artboard.getBoundingClientRect();
    return rect.width === 0 && rect.height === 0
      ? null
      : { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
  }

  /** Starts resizing the artboard from one of its handles. */
  beginArtboardResize(handle: ArtboardHandle, down: PointerEvent): void {
    beginArtboardDrag(this, handle, down);
  }

  /** The box around all the artwork in the artboard's own units, or null when it cannot be read. */
  artworkBounds(): Box | null {
    const root = this.rootSvg();
    if (!root) return null;
    try {
      const box = root.getBBox();
      return box.width > 0 && box.height > 0
        ? { x: box.x, y: box.y, width: box.width, height: box.height }
        : null;
    } catch {
      return null;
    }
  }

  /** Starts dragging a motion path keyframe. */
  beginMotionKeyDrag(elementId: ElementId, keyframeId: string, down: PointerEvent): void {
    this.manipulator.beginMotionKey(elementId, keyframeId, down);
  }

  /** Starts dragging one curve handle of a motion path keyframe. */
  beginMotionHandleDrag(
    elementId: ElementId,
    keyframeId: string,
    side: 'in' | 'out',
    down: PointerEvent,
  ): void {
    this.manipulator.beginMotionHandle(elementId, keyframeId, side, down);
  }

  /** Switches the path through a keyframe between straight and smooth. */
  toggleMotionSmooth(keyframeId: string): void {
    toggleSmooth(this.store, keyframeId);
  }

  /** A press on a point of the path being edited. */
  beginPointDrag(ref: NodeRef, event: PointerEvent): void {
    this.pointEditor.beginPointDrag(ref, event);
  }

  /** A press on a curve handle of the path being edited. */
  beginPointHandleDrag(ref: NodeRef, side: 'in' | 'out', event: PointerEvent): void {
    this.pointEditor.beginHandleDrag(ref, side, event);
  }

  /** Adds a point on a segment of the path being edited. */
  insertPoint(seg: SegmentRef, clientX: number, clientY: number): void {
    this.pointEditor.insertAt(seg, clientX, clientY);
  }

  /** Starts editing the points of a layer. */
  editPoints(id: ElementId): boolean {
    return this.pointEditor.enter(id);
  }

  /** Shows the point editing banner for the current state, and redraws the points. */
  pointEditChanged(): void {
    const id = this.pointEditor.editingId;
    this.pointBanner.hidden = id === null;
    if (id !== null) {
      const morphing = this.pointEditor.keysMorph();
      this.pointBannerText.textContent = morphing
        ? 'Editing points: changes key the shape at the playhead. Esc or Enter to finish.'
        : 'Editing points: changes alter the shape itself. Esc or Enter to finish.';
      this.pointKeyButton.hidden = morphing;
    }
    this.redrawOverlay();
  }

  /** Pauses playback and moves the playhead, so edits land at that time. */
  seek(ms: number): void {
    const playback = this.editing?.playback;
    if (!playback) return;
    if (playback.isPlaying) playback.pause();
    playback.seek(ms);
  }

  /** The elements a manipulation acts on: the selection, or just `id` if it is not in it. */
  private manipulationIds(id: ElementId): ElementId[] {
    const selected = this.store
      .getState()
      .editor.selection.elementIds.filter((s) => !this.isStop(s));
    return selected.includes(id) ? selected : [id];
  }

  /** Opens the pivot marker's menu: reset, or centre on a shape under the pivot. */
  showOriginMenu(id: ElementId, x: number, y: number): void {
    const pivot = this.getOrigin(id);
    const { project } = this.store.getState();
    const items: MenuItem[] = [
      {
        label: 'Reset pivot to layer centre',
        onSelect: () => {
          this.resetOrigin(id);
        },
      },
    ];
    if (pivot) {
      const seen = new Set<ElementId>();
      const under = shapeCandidates(this, id, pivot)
        .filter((c) => c.insideLayer && c.contains && c.id !== id)
        .sort((a, b) => b.depth - a.depth);
      for (const candidate of under) {
        if (seen.has(candidate.id)) continue;
        seen.add(candidate.id);
        const label = project.elements[candidate.id]?.label ?? candidate.id;
        items.push({
          label: `Centre pivot on ${label}`,
          onSelect: () => {
            this.centrePivotOn(id, candidate.id);
          },
        });
      }
    }
    showContextMenu(x, y, items);
  }

  /** Moves the pivot to the centre of a shape's bounding box. */
  centrePivotOn(layerId: ElementId, shapeId: ElementId): void {
    const shape = this.elementsById.get(shapeId);
    const node = this.node(layerId);
    if (!shape || !node) return;
    const box = shapeBoxInLayer(this, layerId, shape);
    if (!box) return;
    const centre = { x: (box.left + box.right) / 2, y: (box.top + box.bottom) / 2 };
    const origin = originFromLocal(node.getBBox(), centre);
    setElementOrigin(this.store, layerId, { x: roundTo(origin.x, 2), y: roundTo(origin.y, 2) });
  }

  /** Finds rendered elements that use the gradient containing a stop. */
  getGradientUsers(stopId: ElementId): Element[] {
    const gradientId = this.elementsById.get(stopId)?.parentElement?.getAttribute('id');
    if (!gradientId) return [];
    const ref = `url(#${gradientId})`;
    return Array.from(this.artboard.querySelectorAll('[data-st-id]')).filter(
      (el) => el.getAttribute('fill') === ref || el.getAttribute('stroke') === ref,
    );
  }

  /** Redraws selection outlines. Called when the playhead, zoom or layout changes. */
  redrawOverlay(): void {
    const { editor } = this.store.getState();
    this.overlay.draw(editor.selection.elementIds, editor.hoveredElementId, this.buildScene());
  }

  /** Gathers the rulers, guides, readout and motion path for the current state. */
  private buildScene(): OverlayScene {
    const { editor } = this.store.getState();
    const subject = this.busy[0] ?? editor.selection.elementIds[0];
    const measuring = this.measureHeld || this.alwaysMeasure || this.busy.length > 0;
    let rulers: Ruler[] = [];
    let shapeId: ElementId | undefined;
    if (measuring && subject !== undefined && !this.isStop(subject)) {
      ({ rulers, shapeId } = computeRulers(this, subject, this.targets));
    }
    this.updateChips(rulers);
    this.updateShapeName(subject, shapeId);

    const single =
      editor.selection.elementIds.length === 1 ? editor.selection.elementIds[0] : undefined;
    const playing = this.editing?.isPlaying() ?? false;
    const motion =
      single !== undefined && !playing && !this.isStop(single)
        ? buildMotionPath(this, single)
        : null;

    const morph = buildMorphGuides(this);
    const points = this.pointEditor.editingId !== null ? this.pointEditor.scene() : null;
    if (rulers.length === 0 && !this.guides && !this.readout && !motion && !morph && !points) {
      return EMPTY_SCENE;
    }
    return {
      rulers,
      guidesX: this.guides?.x ?? [],
      guidesY: this.guides?.y ?? [],
      readout: this.readout,
      motion,
      morph,
      points,
    };
  }

  /** Shows "Centred" chips for any target whose left and right or up and down distances match. */
  private updateChips(rulers: readonly Ruler[]): void {
    const names = { layer: 'Layer', shape: 'Shape', neighbour: 'Nearby', artboard: 'Artboard' };
    const chips = rulers.flatMap((ruler) => {
      if (!ruler.centredX && !ruler.centredY) return [];
      const text =
        ruler.centredX && ruler.centredY
          ? 'Centred'
          : ruler.centredX
            ? 'Centred horizontally'
            : 'Centred vertically';
      return [
        h(
          'span',
          { class: ['measure-chip', `chip-${ruler.target}`] },
          `${names[ruler.target]}: ${text}`,
        ),
      ];
    });
    this.chips.replaceChildren(...chips);
  }

  /** Names the shape being measured in the Measure popover. */
  private updateShapeName(subject: ElementId | undefined, shapeId: ElementId | undefined): void {
    const meta =
      shapeId === undefined ? undefined : this.store.getState().project.elements[shapeId];
    this.shapeName.textContent =
      subject === undefined
        ? 'Select a layer to measure.'
        : meta
          ? `Measuring ${meta.label} (${meta.tag})`
          : this.store.getState().project.elements[subject]?.childIds.length
            ? 'A group measures to its box and other layers.'
            : 'No shape under the pivot.';
  }

  /** Fits the artwork in the viewport, deferring until the viewport has a size. */
  fit(): void {
    const size = this.viewportSize();
    if (size.width === 0 || size.height === 0) {
      this.pendingFit = true;
      return;
    }
    this.pendingFit = false;
    const { viewBox } = this.store.getState().project.svg;
    const view = fitView(viewBox, size);
    setStageView(this.store, view.zoom, view.pan);
  }

  /** Builds the zoom and background controls above the viewport. */
  private buildToolbar(): HTMLElement {
    this.zoomLabel = h(
      'button',
      { type: 'button', class: 'zoom-label', title: 'Set zoom' },
      '100%',
    );
    this.zoomLabel.addEventListener('click', () => {
      this.editZoomLabel();
    });
    const background = h(
      'button',
      { type: 'button', 'aria-pressed': 'false', title: 'Toggle checkerboard' },
      'Checker',
    );
    background.addEventListener('click', () => {
      const plain = this.viewport.classList.toggle('stage-bg-plain');
      this.viewport.classList.toggle('stage-bg-checker', !plain);
      background.setAttribute('aria-pressed', String(!plain));
    });
    background.setAttribute('aria-pressed', 'true');

    return h(
      'div',
      { class: 'stage-toolbar', role: 'toolbar', 'aria-label': 'Stage controls' },
      ...this.buildToolButtons(),
      h('span', { class: 'toolbar-divider' }),
      h(
        'button',
        {
          type: 'button',
          'aria-label': 'Zoom out',
          title: 'Zoom out',
          onClick: () => {
            this.zoomBy(1 / ZOOM_STEP);
          },
        },
        '−',
      ),
      this.zoomLabel,
      h(
        'button',
        {
          type: 'button',
          'aria-label': 'Zoom in',
          title: 'Zoom in',
          onClick: () => {
            this.zoomBy(ZOOM_STEP);
          },
        },
        '+',
      ),
      h(
        'button',
        {
          type: 'button',
          title: 'Fit to stage (Ctrl + 0)',
          onClick: () => {
            this.fit();
          },
        },
        'Fit',
      ),
      h(
        'button',
        {
          type: 'button',
          title: 'Actual size',
          onClick: () => {
            this.actualSize();
          },
        },
        '100%',
      ),
      h('span', { class: 'topbar-spacer' }),
      this.buildMeasureButton(),
      background,
    );
  }

  /** Builds the Select, Rotate and Scale tool buttons. */
  private buildToolButtons(): HTMLButtonElement[] {
    return TOOLS.map((tool) => {
      const button = h(
        'button',
        {
          type: 'button',
          'aria-pressed': String(tool.id === this.tool),
          title: `${tool.label} tool (${tool.key})`,
          onClick: () => {
            this.setTool(tool.id);
          },
        },
        tool.label,
      );
      this.toolButtons.set(tool.id, button);
      return button;
    });
  }

  /** Switches the active tool. */
  setTool(tool: Tool): void {
    this.tool = tool;
    for (const [id, button] of this.toolButtons)
      button.setAttribute('aria-pressed', String(id === tool));
    this.viewport.dataset['tool'] = tool;
  }

  /** The active tool. */
  getTool(): Tool {
    return this.tool;
  }

  /** Builds the Measure button and its popover of ruler targets. */
  private buildMeasureButton(): HTMLElement {
    const button = h(
      'button',
      { type: 'button', title: 'Pivot rulers: hold 4 to measure from the pivot' },
      'Measure',
    );
    this.shapeName = h('div', { class: 'measure-shape-name' }, 'Select a layer to measure.');
    /** A labelled checkbox that updates one setting. */
    const option = (
      label: string,
      checked: boolean,
      change: (on: boolean) => void,
    ): HTMLElement => {
      const box = h('input', { type: 'checkbox', checked });
      box.addEventListener('change', () => {
        change(box.checked);
        this.redrawOverlay();
      });
      return h('label', { class: 'measure-option' }, box, ` ${label}`);
    };
    const content = h(
      'div',
      { class: 'measure-popover' },
      option('Layer box', this.targets.layer, (on) => {
        this.targets.layer = on;
      }),
      option('Shape under the pivot', this.targets.shape, (on) => {
        this.targets.shape = on;
      }),
      this.shapeName,
      option('Nearest other shapes', this.targets.neighbour, (on) => {
        this.targets.neighbour = on;
      }),
      option('Artboard', this.targets.artboard, (on) => {
        this.targets.artboard = on;
      }),
      h('hr'),
      option('Always measure', this.alwaysMeasure, (on) => {
        this.alwaysMeasure = on;
        writeStorage(MEASURE_KEY, on ? 'on' : 'off');
      }),
      h(
        'p',
        { class: 'measure-hint' },
        'Hold 4 to measure, and use the arrow keys to nudge the pivot.',
      ),
    );
    const popover = createPopover(button, content, 'Measure');
    button.addEventListener('click', () => {
      this.redrawOverlay();
      popover.open();
    });
    return h('span', { class: 'measure-wrap' }, button, popover.el);
  }

  /** Swaps the zoom label for a number input and applies the typed percentage. */
  private editZoomLabel(): void {
    const input = h('input', {
      type: 'number',
      class: 'zoom-input',
      min: 5,
      max: 3200,
      'aria-label': 'Zoom percentage',
      value: Math.round(this.store.getState().editor.stageZoom * 100),
    });
    this.zoomLabel.replaceWith(input);
    input.focus();
    input.select();
    let done = false;
    /** Restores the label, optionally applying the typed zoom around the viewport centre. */
    const finish = (apply: boolean): void => {
      if (done) return;
      done = true;
      const percent = Number(input.value);
      input.replaceWith(this.zoomLabel);
      if (apply && Number.isFinite(percent) && percent > 0) this.zoomTo(percent / 100);
    };
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') finish(true);
      else if (event.key === 'Escape') finish(false);
    });
    input.addEventListener('blur', () => {
      finish(true);
    });
  }

  /** Current viewport size in CSS pixels. */
  private viewportSize(): Size {
    return { width: this.viewport.clientWidth, height: this.viewport.clientHeight };
  }

  /** Viewport centre in viewport coordinates. */
  private centre(): Vec2 {
    const size = this.viewportSize();
    return { x: size.width / 2, y: size.height / 2 };
  }

  /** Multiplies the zoom around the viewport centre. */
  private zoomBy(factor: number): void {
    this.zoomTo(this.store.getState().editor.stageZoom * factor);
  }

  /** Sets the zoom around the viewport centre. */
  private zoomTo(zoom: number): void {
    const { stageZoom, stagePan } = this.store.getState().editor;
    const next = zoomAt({ zoom: stageZoom, pan: stagePan }, zoom, this.centre());
    setStageView(this.store, next.zoom, next.pan);
  }

  /** Shows the artwork at 100%, centred. */
  private actualSize(): void {
    const view = centreView(this.store.getState().project.svg.viewBox, this.viewportSize(), 1);
    setStageView(this.store, view.zoom, view.pan);
  }

  /** Subscribes to the store slices the stage renders from. */
  private bindStore(): void {
    // A new project refits the view; edits to the artwork and the artboard keep the zoom.
    this.select(
      (s) => s.project.id,
      () => {
        this.fit();
      },
    );
    this.select(
      (s) => s.project.svg.background,
      (background) => {
        this.artboard.style.backgroundColor = background ?? '';
      },
      { immediate: true },
    );
    this.select(
      (s) => [s.editor.stageZoom, s.editor.stagePan] as const,
      ([zoom, pan]) => {
        this.applyView(zoom, pan);
      },
      { equality: 'shallow', immediate: true },
    );
    this.select(
      (s) =>
        Object.values(s.project.elements)
          .filter((e) => e.hiddenInEditor)
          .map((e) => e.id)
          .join(','),
      (ids) => {
        this.hiddenStyle.textContent = ids
          ? ids
              .split(',')
              .map((id) => `[data-st-id="${id}"] { visibility: hidden; }`)
              .join('\n')
          : '';
        this.redrawOverlay();
      },
      { immediate: true },
    );
    this.select(
      (s) =>
        [
          s.editor.selection.elementIds,
          s.editor.selection.keyframeIds,
          s.editor.hoveredElementId,
          s.editor.playhead,
          s.project.elements,
        ] as const,
      ([selected]) => {
        // Selecting anything else ends point editing.
        const editing = this.pointEditor.editingId;
        if (editing !== null && !(selected.length === 1 && selected[0] === editing)) {
          this.pointEditor.exit();
        }
        this.redrawOverlay();
      },
      { equality: 'shallow' },
    );
  }

  /**
   * When the frame's top left corner moves, shifts the pan by the same amount so the artwork stays
   * where it is on screen. This runs as the new scene is shown, so the pan and the markup change
   * together. A different project is left to the fit.
   */
  private keepArtworkInPlace(viewBox: Box): void {
    const { project, editor } = this.store.getState();
    const previous = this.sceneFrame;
    this.sceneFrame = { projectId: project.id, x: viewBox.x, y: viewBox.y };
    if (!previous || previous.projectId !== project.id) return;
    const dx = (viewBox.x - previous.x) * editor.stageZoom;
    const dy = (viewBox.y - previous.y) * editor.stageZoom;
    if (dx === 0 && dy === 0) return;
    setStageView(this.store, editor.stageZoom, {
      x: editor.stagePan.x + dx,
      y: editor.stagePan.y + dy,
    });
  }

  /** Applies zoom and pan to the artboard and refreshes the zoom readout. */
  private applyView(zoom: number, pan: Vec2): void {
    this.artboard.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
    this.zoomLabel.textContent = `${Math.round(zoom * 100)}%`;
    this.redrawOverlay();
  }

  /** Returns the element that holds the rendered scene, which animations are attached to. */
  getArtboard(): HTMLElement {
    return this.artboard;
  }

  /** Registers a function to call after the rendered markup has been replaced. */
  onSceneReplaced(listener: () => void): () => void {
    this.sceneChangeListeners.add(listener);
    return () => {
      this.sceneChangeListeners.delete(listener);
    };
  }

  /**
   * Shows a compiled scene. The markup is only re-inserted when it changed, which happens when
   * an element becomes animated or gains a wrapper, not on every keyframe edit; the CSS is
   * updated whenever its text changes.
   */
  setScene(scene: { markup: string; css: string; sceneClass: string; viewBox: Box }): void {
    this.keepArtworkInPlace(scene.viewBox);
    this.artboard.className = `artboard ${scene.sceneClass}`;
    this.artboard.style.width = `${scene.viewBox.width}px`;
    this.artboard.style.height = `${scene.viewBox.height}px`;
    const resized =
      scene.viewBox.width !== this.lastSize.width || scene.viewBox.height !== this.lastSize.height;
    this.lastSize = { width: scene.viewBox.width, height: scene.viewBox.height };
    if (this.compiledStyle.textContent !== scene.css) this.compiledStyle.textContent = scene.css;
    if (scene.markup === this.lastMarkup) {
      if (resized) this.redrawOverlay();
      return;
    }

    this.lastMarkup = scene.markup;
    // The markup comes from the compiler, which starts from sanitised source.
    this.artboard.innerHTML = scene.markup;
    this.elementsById.clear();
    for (const el of Array.from(this.artboard.querySelectorAll('[data-st-id]'))) {
      const id = el.getAttribute('data-st-id');
      if (id) this.elementsById.set(id, el);
    }
    this.measurePathLengths();
    this.redrawOverlay();
    for (const listener of this.sceneChangeListeners) listener();
  }

  /** Stores each geometry element's total length, which the line-drawing property needs. */
  private measurePathLengths(): void {
    const lengths = new Map<ElementId, number>();
    for (const [id, el] of this.elementsById) {
      if (!GEOMETRY_TAGS.includes(el.localName)) continue;
      try {
        const length = (el as SVGGeometryElement).getTotalLength();
        if (Number.isFinite(length)) lengths.set(id, length);
      } catch {
        // Some elements cannot report a length (for example when not rendered).
      }
    }
    if (lengths.size === 0) return;
    this.store.updateProjectSilently((draft) => {
      for (const [id, length] of lengths) {
        const element = draft.elements[id];
        if (element && element.pathLength !== length) element.pathLength = length;
      }
    });
  }

  /** Finds a shape under a point by its geometry, so shapes with no fill can be picked inside. */
  private geometryAt(x: number, y: number): ElementId | undefined {
    const entries = [...this.elementsById].reverse();
    for (const [id, el] of entries) {
      if (this.isStop(id) || !isGeometry(el)) continue;
      if (containsPoint(this, el, { x, y })) return id;
    }
    return undefined;
  }

  /** Finds the nearest element with a stable ID under a screen point. */
  private hitTest(x: number, y: number): ElementId | undefined {
    for (const el of this.shadow.elementsFromPoint(x, y)) {
      const id = el.closest('[data-st-id]')?.getAttribute('data-st-id');
      if (id && this.store.getState().project.elements[id]?.tag !== 'stop') return id;
    }
    return undefined;
  }

  /** Resolves a click on an element to the element it should select. */
  private target(hitId: ElementId, kind: ClickKind): ElementId | undefined {
    const { project, editor } = this.store.getState();
    return resolveSelectionTarget(project.elements, hitId, editor.selection.elementIds, kind);
  }

  /** Whether a layer is a shape or path, which has points to edit. */
  private isEditableShape(id: ElementId): boolean {
    return GEOMETRY_TAGS.includes(this.store.getState().project.elements[id]?.tag ?? '');
  }

  /** Whether an element is live text whose words can be edited in the Inspector. */
  private isText(id: ElementId): boolean {
    return TEXT_TAGS.has(this.store.getState().project.elements[id]?.tag ?? '');
  }

  /**
   * Keys for point editing: Enter starts it on a selected shape (while the stage or nothing has
   * focus); while editing, Escape and Enter finish, Delete and Backspace remove the selected
   * points, and the arrow keys nudge them. Returns whether the key was used.
   */
  private handlePointKey(event: KeyboardEvent): boolean {
    if (event.ctrlKey || event.metaKey) return false;
    const editing = this.pointEditor.editingId;
    if (editing === null) {
      if (event.key !== 'Enter' || event.altKey || event.shiftKey) return false;
      const target = event.target;
      const onStage =
        target === document.body || (target instanceof Node && this.viewport.contains(target));
      const selected = this.store.getState().editor.selection.elementIds;
      const only = selected.length === 1 ? selected[0] : undefined;
      if (!onStage || only === undefined || !this.isEditableShape(only)) return false;
      return this.pointEditor.enter(only);
    }
    if (event.key === 'Escape' || event.key === 'Enter') {
      this.pointEditor.exit();
      return true;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      this.pointEditor.deleteSelected();
      return true;
    }
    const step = ARROW_STEPS[event.key];
    if (step && this.pointEditor.hasSelection()) {
      this.pointEditor.nudge(step.x, step.y, event.shiftKey, `points-nudge:${editing}`);
      return true;
    }
    return false;
  }

  /**
   * Stage keys: 1, 2 and 3 pick a tool, 4 held shows the pivot rulers, and the arrow keys nudge.
   * The pivot is nudged while 4 is held or the pivot marker has focus; otherwise the selection
   * is nudged, but only while the stage itself has focus, so frame stepping works elsewhere.
   * This runs before the global shortcuts, which would otherwise step frames.
   */
  private handleKey(event: KeyboardEvent): void {
    if (this.picking && event.key === 'Escape') {
      event.stopPropagation();
      this.finishPick(null);
      return;
    }
    if (this.handlePointKey(event)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.ctrlKey || event.metaKey) return;
    const key = event.key;
    const ids = this.store.getState().editor.selection.elementIds.filter((id) => !this.isStop(id));
    const step = ARROW_STEPS[key];

    if (!event.altKey && !step) {
      const lower = key.toLowerCase();
      const tool = TOOLS.find((t) => t.key.toLowerCase() === lower);
      if (tool) {
        this.setTool(tool.id);
        return;
      }
      if (lower === MEASURE_HOLD_KEY && !this.measureHeld) {
        this.measureHeld = true;
        this.redrawOverlay();
      }
      return;
    }
    if (!step || ids.length === 0) return;

    const target = event.target instanceof Element ? event.target : null;
    const markerFor = target?.closest('.origin-marker')?.getAttribute('data-origin-for');
    const primary = markerFor ?? ids[0];
    if ((this.measureHeld || markerFor) && primary !== undefined) {
      const size = event.altKey ? 0.1 : event.shiftKey ? 10 : 1;
      this.manipulator.nudgeOrigin(primary, step.x * size, step.y * size);
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.altKey || !target || !this.viewport.contains(target)) return;
    const size = event.shiftKey ? 10 : 1;
    this.manipulator.nudge(ids, step.x * size, step.y * size);
    event.preventDefault();
    event.stopPropagation();
  }

  /** Registers pointer, wheel and keyboard handling. */
  private bindInput(): void {
    const viewport = this.viewport;

    this.listen(
      viewport,
      'wheel',
      (event) => {
        event.preventDefault();
        const rect = viewport.getBoundingClientRect();
        const { stageZoom, stagePan } = this.store.getState().editor;
        if (event.ctrlKey || event.metaKey) {
          const next = zoomAt(
            { zoom: stageZoom, pan: stagePan },
            stageZoom * wheelZoomFactor(event.deltaY),
            { x: event.clientX - rect.left, y: event.clientY - rect.top },
          );
          setStageView(this.store, next.zoom, next.pan);
          return;
        }
        const dx = event.deltaX !== 0 ? event.deltaX : event.shiftKey ? event.deltaY : 0;
        const dy = event.shiftKey ? 0 : event.deltaY;
        setStageView(this.store, stageZoom, { x: stagePan.x - dx, y: stagePan.y - dy });
      },
      { passive: false },
    );

    this.listen(viewport, 'pointerdown', (event) => {
      if (this.picking && event.button === 0) {
        event.preventDefault();
        const hit =
          this.hitTest(event.clientX, event.clientY) ??
          this.geometryAt(event.clientX, event.clientY);
        if (hit !== undefined) {
          this.finishPick({
            id: hit,
            hide: this.pickHide.checked,
            placement: this.pickPlacement.value === 'world' ? 'world' : 'in-place',
          });
        }
        return;
      }
      if (event.button === 1) {
        event.preventDefault();
        viewport.setPointerCapture(event.pointerId);
        this.panDrag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
        return;
      }
      if (event.button !== 0) return;
      viewport.focus({ preventScroll: true });
      // A press anywhere but on a point or handle finishes point editing.
      if (this.pointEditor.editingId !== null) {
        this.pointEditor.exit();
        return;
      }
      const selected = this.store.getState().editor.selection.elementIds;
      const movable = selected.filter((id) => !this.isStop(id));
      if (this.tool !== 'select' && movable.length > 0) {
        event.preventDefault();
        if (this.tool === 'rotate') this.manipulator.beginRotate(movable, event);
        else this.manipulator.beginScale(movable, 'both', event);
        return;
      }
      const hit = this.hitTest(event.clientX, event.clientY);
      if (hit === undefined) {
        if (!event.shiftKey) clearSelection(this.store);
        return;
      }
      const target = this.target(hit, event.ctrlKey || event.metaKey ? 'deep' : 'click');
      if (target === undefined) return;
      if (event.shiftKey) {
        selectElements(this.store, toggleInSelection(selected, target));
        return;
      }
      // Pressing on something already selected keeps a multiple selection so it moves together.
      if (!selected.includes(target)) selectElements(this.store, [target]);
      const now = this.store
        .getState()
        .editor.selection.elementIds.filter((id) => !this.isStop(id));
      this.manipulator.beginMove(now, event);
    });

    this.listen(viewport, 'pointermove', (event) => {
      if (this.panDrag?.pointerId === event.pointerId) {
        const { stageZoom, stagePan } = this.store.getState().editor;
        const dx = event.clientX - this.panDrag.x;
        const dy = event.clientY - this.panDrag.y;
        this.panDrag.x = event.clientX;
        this.panDrag.y = event.clientY;
        setStageView(this.store, stageZoom, { x: stagePan.x + dx, y: stagePan.y + dy });
        return;
      }
      if (this.hoverFrame) return;
      const { clientX, clientY, ctrlKey } = event;
      this.hoverFrame = requestAnimationFrame(() => {
        this.hoverFrame = 0;
        const hit = this.hitTest(clientX, clientY);
        setHovered(
          this.store,
          hit === undefined ? null : (this.target(hit, ctrlKey ? 'deep' : 'click') ?? null),
        );
      });
    });

    this.listen(viewport, 'pointerup', (event) => {
      if (this.panDrag?.pointerId === event.pointerId) this.panDrag = null;
    });
    this.listen(viewport, 'pointerleave', () => {
      setHovered(this.store, null);
    });

    this.listen(viewport, 'dblclick', (event) => {
      const hit = this.hitTest(event.clientX, event.clientY);
      if (hit === undefined) return;
      const target = this.target(hit, 'drill');
      if (target === undefined) return;
      // Double-clicking a shape that is already selected on its own edits its points.
      const selected = this.store.getState().editor.selection.elementIds;
      if (selected.length === 1 && selected[0] === target && this.isEditableShape(target)) {
        this.pointEditor.enter(target);
        return;
      }
      // Double-clicking text that is already selected on its own edits its words.
      if (selected.length === 1 && selected[0] === target && this.isText(target)) {
        requestTextEditing();
        return;
      }
      selectElements(this.store, [target]);
    });

    this.listen(
      document,
      'keydown',
      (event) => {
        if (isTextEntryTarget(event.target)) return;
        if ((event.ctrlKey || event.metaKey) && event.key === '0') {
          event.preventDefault();
          this.fit();
          return;
        }
        this.handleKey(event);
      },
      { capture: true },
    );
    this.listen(document, 'keyup', (event) => {
      if (event.key === MEASURE_HOLD_KEY && this.measureHeld) {
        this.measureHeld = false;
        this.redrawOverlay();
      }
    });
    this.listen(window, 'blur', () => {
      if (this.measureHeld) {
        this.measureHeld = false;
        this.redrawOverlay();
      }
    });
  }
}
